// Pure helpers for the Catalog page's article list and article dialog (#2007).
import type { WorkflowBusiness } from "@/api";

export type CatalogItem = WorkflowBusiness["snapshot"]["catalog"][number];

export interface CatalogGroup {
  /** Stable React key; the product group's name, or a fixed key for articles without one. */
  key: string;
  /** `null` for the articles that carry no product group. */
  name: string | null;
  items: CatalogItem[];
}

const NO_GROUP_KEY = "\u0000none";

function groupName(item: CatalogItem): string | null {
  const name = typeof item.group === "string" ? item.group.trim() : "";
  return name || null;
}

/** The server's own row, which carries what the typed projection leaves out. */
function sourceRecord(item: CatalogItem): { nameEn?: unknown; raw?: unknown } {
  const record: unknown = item.source_record;
  return record && typeof record === "object" ? record : {};
}

/** The server marks an article an admin added by hand; only those can be switched off here. */
export function isManualItem(item: CatalogItem): boolean {
  const raw = sourceRecord(item).raw;
  return !!raw && typeof raw === "object" && !Array.isArray(raw)
    && (raw as { source?: unknown }).source === "manual";
}

export function englishName(item: CatalogItem): string | null {
  const name = sourceRecord(item).nameEn;
  return typeof name === "string" && name.trim() && name.trim() !== item.name ? name.trim() : null;
}

function matches(item: CatalogItem, needle: string): boolean {
  if (!needle) return true;
  return [item.name, item.bp_id, item.group, item.subgroup, englishName(item)]
    .some((value) => typeof value === "string" && value.toLowerCase().includes(needle));
}

/**
 * Every article under its product group. Groups run alphabetically, with the articles that have no
 * group last; inside a group the active articles come first, then by name. A search keeps only the
 * matching articles, so a group in which nothing matches is not listed.
 */
export function groupCatalog(items: readonly CatalogItem[], query: string): CatalogGroup[] {
  const needle = query.trim().toLowerCase();
  const groups = new Map<string, CatalogGroup>();
  for (const item of items) {
    if (!matches(item, needle)) continue;
    const name = groupName(item);
    const key = name ?? NO_GROUP_KEY;
    const group = groups.get(key) ?? { key, name, items: [] };
    group.items.push(item);
    groups.set(key, group);
  }
  const sorted = [...groups.values()].sort((left, right) => {
    if (left.name === null || right.name === null) return left.name === right.name ? 0 : left.name === null ? 1 : -1;
    return left.name.localeCompare(right.name, "de");
  });
  for (const group of sorted) {
    group.items.sort((left, right) =>
      Number(right.active) - Number(left.active) || left.name.localeCompare(right.name, "de"));
  }
  return sorted;
}

/** The product groups in use, for the group field of a new article. */
export function catalogGroupNames(items: readonly CatalogItem[]): string[] {
  return [...new Set(items.map(groupName).filter((name): name is string => name !== null))]
    .sort((left, right) => left.localeCompare(right, "de"));
}

export function catalogSummary(items: readonly CatalogItem[]) {
  return {
    total: items.length,
    active: items.filter((item) => item.active).length,
    groups: catalogGroupNames(items).length,
    packages: items.filter((item) => item.active && item.is_package).length,
  };
}

/**
 * A net price as an admin types it — "12", "12,5", "12.50" — in cents. `null` when the text is not
 * an amount; an empty field is the caller's "price unknown", not an amount.
 */
export function centsFromEuroText(text: string): number | null {
  const match = /^(\d{1,9})(?:[.,](\d{1,2}))?$/.exec(text.trim());
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
}

export function euroTextFromCents(minor: number | null): string {
  return minor === null ? "" : (minor / 100).toFixed(2).replace(".", ",");
}

/** A VAT rate in whole percent, 0–100. `null` when the text is not one. */
export function vatRateFromText(text: string): number | null {
  if (!/^\d{1,3}$/.test(text.trim())) return null;
  const rate = Number(text.trim());
  return rate <= 100 ? rate : null;
}
